import { useState } from "react";
import { HelpCircleIcon, MoreVerticalIcon, XIcon } from "lucide-react";

interface MicCameraPermissionGuideProps {
    onDismiss?: () => void;
}

/**
 * Mobile-only helper that walks the candidate through enabling
 * camera & microphone permissions in their browser.
 *
 * Same self-gating pattern as `BrowserCompatibilityWarning` — uses
 * Tailwind's `sm:hidden` so the banner is rendered identically on the
 * server and the client and the CSS layer hides it on tablet/desktop
 * (≥ 640px).
 */
export default function MicCameraPermissionGuide({
    onDismiss,
}: MicCameraPermissionGuideProps) {
    const [dismissed, setDismissed] = useState<boolean>(false);

    if (dismissed) return null;

    const handleDismiss = () => {
        setDismissed(true);
        onDismiss?.();
    };

    return (
        <div className="hidden bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg mb-4">
            <div className="flex items-center gap-2.5 px-3 py-2.5">
                <HelpCircleIcon className="h-4 w-4 text-blue-600 dark:text-blue-300 flex-shrink-0" />
                <span className="flex-1 text-xs font-medium text-blue-800 dark:text-blue-200 leading-relaxed">
                    Allow camera &amp; microphone access
                </span>
                <button
                    type="button"
                    onClick={handleDismiss}
                    className="flex-shrink-0 self-start p-0.5 rounded text-blue-700 dark:text-blue-300 hover:bg-blue-100 dark:hover:bg-blue-800/40 transition-colors"
                    aria-label="Dismiss"
                >
                    <XIcon className="h-3.5 w-3.5" />
                </button>
            </div>

            <div className="px-3 pb-3 pt-1 border-t border-blue-100 dark:border-blue-800">
                <ol className="text-xs text-blue-900 dark:text-blue-100 leading-relaxed space-y-1.5 list-decimal pl-4 mt-2">
                    <li>
                        Tap the
                        <span className="inline-flex items-center justify-center align-middle mx-1 w-5 h-5 rounded bg-blue-100 dark:bg-blue-800 text-blue-700 dark:text-blue-200">
                            <MoreVerticalIcon className="w-3.5 h-3.5" />
                        </span>
                        next to your address bar in your browser, then open <strong>Settings</strong>.
                    </li>
                    <li>
                        Allow both <strong>Camera</strong> and <strong>Microphone</strong> permissions.
                    </li>
                    <li>
                        Refresh the page to begin the interview.
                    </li>
                </ol>
            </div>
        </div>
    );
}
